/**
 * 消失点/透视网格工具（M4-07 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 *
 * 交互分两段：先点 4 个角点定义透视平面（同 M4-02 的透视裁剪手势），
 * 四点齐后进入"平面内绘制"——在角点手柄之外按下/拖动会在平面局部画布上
 * 画一个圆形色块（简化版笔刷：固定半径，用当前前景色），实时按透视 warp
 * 叠加预览；Enter 提交（一条历史，混合进活动 raster 图层），Esc 取消。
 */
import {
  createVanishingPointPlane,
  planeDocumentBounds,
  planeGridLines,
  documentToPlane,
  samplePatchAtDocumentPoint,
  warpPatchIntoDocument,
  type VanishingPointPlane,
} from '../../engine/transform/vanishing-point';
import type { Point } from '../../engine/transform/perspective';
import { TILE, tileKey } from '../../engine/tile/key';
import { BYTES_PER_TILE } from '../../engine/tile/store';
import type { AppStore, RgbColor } from '../../app/store';
import type { ToolManager } from './registry';

export interface VanishingPointDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
  readonly color: () => RgbColor;
}

const HANDLE_HIT_RADIUS = 10;
/** 简化笔刷：固定半径（平面局部坐标系像素）。 */
const STAMP_RADIUS = 14;

export class VanishingPointToolController {
  readonly #deps: VanishingPointDeps;
  #corners: Point[] = [];
  #dragIndex = -1;
  #plane: VanishingPointPlane | null = null;
  #patch: Uint8Array | null = null;
  #painting = false;

  constructor(deps: VanishingPointDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#corners.length > 0;
  }

  #rebuildPlane(): void {
    if (this.#corners.length !== 4) return;
    const corners = this.#corners as [Point, Point, Point, Point];
    const prevPlane = this.#plane;
    const prevPatch = this.#patch;
    this.#plane = createVanishingPointPlane(corners);
    const needed = this.#plane.planeWidth * this.#plane.planeHeight * 4;
    if (prevPatch === null || prevPlane === null || prevPatch.length !== needed) {
      // 拖角点会改变对边长度→改变 planeWidth/Height：旧 patch 尺寸对不上新平面，
      // 必须重新分配，否则 stampAt/warpPatchIntoDocument 会用新 planeWidth
      // 去索引旧缓冲，越界读写在 TypedArray 上不报错、只会静默产生 NaN/丢像素。
      const next = new Uint8Array(needed);
      if (prevPatch !== null && prevPlane !== null) {
        // 尽量保留已画的内容：按行拷贝新旧尺寸的重叠区域
        const copyWidth = Math.min(prevPlane.planeWidth, this.#plane.planeWidth);
        const copyHeight = Math.min(prevPlane.planeHeight, this.#plane.planeHeight);
        for (let y = 0; y < copyHeight; y++) {
          const srcStart = y * prevPlane.planeWidth * 4;
          const dstStart = y * this.#plane.planeWidth * 4;
          next.set(prevPatch.subarray(srcStart, srcStart + copyWidth * 4), dstStart);
        }
      }
      this.#patch = next;
    }
  }

  #stampAt(local: Point): void {
    const patch = this.#patch;
    const plane = this.#plane;
    if (patch === null || plane === null) return;
    const color = this.#deps.color();
    const r0 = Math.max(0, Math.floor(local.x - STAMP_RADIUS));
    const r1 = Math.min(plane.planeWidth - 1, Math.ceil(local.x + STAMP_RADIUS));
    const t0 = Math.max(0, Math.floor(local.y - STAMP_RADIUS));
    const t1 = Math.min(plane.planeHeight - 1, Math.ceil(local.y + STAMP_RADIUS));
    for (let y = t0; y <= t1; y++) {
      for (let x = r0; x <= r1; x++) {
        const d = Math.hypot(x - local.x, y - local.y);
        if (d > STAMP_RADIUS) continue;
        const at = (y * plane.planeWidth + x) * 4;
        patch[at] = color.r;
        patch[at + 1] = color.g;
        patch[at + 2] = color.b;
        patch[at + 3] = 255;
      }
    }
  }

  handlePointer(phase: 'down' | 'move' | 'up' | 'double', point: Point): boolean {
    if (this.#deps.tools.active.id !== 'vanishingPoint') return false;
    if (this.#deps.store.document === null) return true;

    if (this.#corners.length < 4) {
      if (phase === 'down') {
        this.#corners.push(point);
        if (this.#corners.length === 4) this.#rebuildPlane();
        this.#deps.requestRender();
      }
      return true;
    }

    if (phase === 'down') {
      const hit = this.#corners.findIndex((c) => Math.hypot(c.x - point.x, c.y - point.y) <= HANDLE_HIT_RADIUS);
      if (hit >= 0) {
        this.#dragIndex = hit;
        return true;
      }
      this.#painting = true;
      if (this.#plane !== null) this.#stampAt(documentToPlane(this.#plane, point));
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'move') {
      if (this.#dragIndex >= 0) {
        this.#corners[this.#dragIndex] = point;
        this.#rebuildPlane();
        this.#deps.requestRender();
        return true;
      }
      if (this.#painting && this.#plane !== null) {
        this.#stampAt(documentToPlane(this.#plane, point));
        this.#deps.requestRender();
        return true;
      }
      return true;
    }
    if (phase === 'up') {
      this.#dragIndex = -1;
      this.#painting = false;
      return true;
    }
    return true;
  }

  /** Enter：把平面局部画布 warp 混合进活动 raster 图层。返回是否发生了写入。 */
  commit(): boolean {
    const plane = this.#plane;
    const patch = this.#patch;
    const store = this.#deps.store;
    if (plane === null || patch === null) {
      this.cancel();
      return false;
    }
    const target = store.activeRasterLayerId();
    const document = store.document;
    if (target === null || document === null) {
      this.cancel();
      return false;
    }

    const { width, height } = document.size;
    const full = new Uint8Array(width * height * 4);
    const bbox = warpPatchIntoDocument(plane, patch, full, width, height);
    if (bbox === null) {
      this.cancel();
      return false;
    }

    const transaction = store.beginPixelTransaction('消失点绘制');
    const tx0 = Math.floor(bbox.x / TILE);
    const ty0 = Math.floor(bbox.y / TILE);
    const tx1 = Math.floor((bbox.x + bbox.width - 1) / TILE);
    const ty1 = Math.floor((bbox.y + bbox.height - 1) / TILE);
    const keys: Array<{ tx: number; ty: number; key: ReturnType<typeof tileKey> }> = [];
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        const key = tileKey(target, tx, ty);
        transaction.touch(key);
        keys.push({ tx, ty, key });
      }
    }
    transaction.capture();

    const dirty: Array<{ tx: number; ty: number }> = [];
    for (const { tx, ty, key } of keys) {
      const originX = tx * TILE;
      const originY = ty * TILE;
      const existing = store.tiles.get(key)?.data;
      const out = existing === null || existing === undefined ? new Uint8Array(BYTES_PER_TILE) : existing.slice();
      // 只有真的贴上了不透明像素才算“动过”——起点是 false，不是"这块本来
      // 就有数据"。种成 true 会让"4 角点后没画一笔就 Enter"把背景原样
      // 写回也当成改动，凭空生出一条什么都没变的历史（空操作不该进历史）。
      let any = false;
      for (let y = 0; y < TILE; y++) {
        const docY = originY + y;
        if (docY < bbox.y || docY >= bbox.y + bbox.height || docY >= height) continue;
        for (let x = 0; x < TILE; x++) {
          const docX = originX + x;
          if (docX < bbox.x || docX >= bbox.x + bbox.width || docX >= width) continue;
          const from = (docY * width + docX) * 4;
          if (full[from + 3] === 0) continue;
          const to = (y * TILE + x) * 4;
          out[to] = full[from]!;
          out[to + 1] = full[from + 1]!;
          out[to + 2] = full[from + 2]!;
          out[to + 3] = full[from + 3]!;
          any = true;
        }
      }
      if (any) {
        store.tiles.set(key, out);
        dirty.push({ tx, ty });
      }
    }

    if (dirty.length === 0) {
      // 4 角点后没画一笔（patch 全透明）：不留一条什么都没变的历史
      this.cancel();
      return false;
    }
    store.commitPixels('消失点绘制', transaction.end());
    store.invalidatePixels(dirty);
    this.cancel();
    return true;
  }

  cancel(): void {
    this.#corners = [];
    this.#dragIndex = -1;
    this.#plane = null;
    this.#patch = null;
    this.#painting = false;
    this.#deps.requestRender();
  }

  /**
   * 平面局部画布（`#patch`）按透视 warp 到文档空间，画一张跟提交后
   * 效果完全一致的实时预览——`#stampAt` 只改内部缓冲，真正让人在拖拽时
   * "看见"画出来的东西，得靠这一步（否则外面看到的就是"拖了但什么都
   * 没变"：真实 bug，`#patch` 确实被写了，只是从没被画到屏幕上）。
   * 采样函数跟 `commit()` 走的 `warpPatchIntoDocument` 同一个
   * `samplePatchAtDocumentPoint`，预览和最终结果不会对不上。
   */
  #drawPatchPreview(ctx: CanvasRenderingContext2D, plane: VanishingPointPlane): void {
    const patch = this.#patch;
    if (patch === null) return;
    // 交互层单测跑在 node 环境（浏览器手势见 e2e），没有真的 DOM——
    // 跟 brush-tool.ts/heal-tool.ts 等同一个防御性写法
    if (typeof document === 'undefined') return;
    const bounds = planeDocumentBounds(plane);
    if (bounds.width <= 0 || bounds.height <= 0) return;

    const offscreen = document.createElement('canvas');
    offscreen.width = bounds.width;
    offscreen.height = bounds.height;
    const offCtx = offscreen.getContext('2d');
    if (offCtx === null) return;
    const image = offCtx.createImageData(bounds.width, bounds.height);
    for (let y = 0; y < bounds.height; y++) {
      for (let x = 0; x < bounds.width; x++) {
        const at = (y * bounds.width + x) * 4;
        const sampled = samplePatchAtDocumentPoint(plane, patch, bounds.x + x, bounds.y + y);
        if (sampled === null) continue; // ImageData 初始全 0（透明），不用另外清
        image.data[at] = sampled[0];
        image.data[at + 1] = sampled[1];
        image.data[at + 2] = sampled[2];
        image.data[at + 3] = sampled[3];
      }
    }
    offCtx.putImageData(image, 0, 0);
    // drawImage（不同于 putImageData）会跟着 ctx 当前的视口变换走，
    // 文档坐标 bounds.x/y 在缩放/平移后依然落在正确的屏幕位置上
    ctx.drawImage(offscreen, bounds.x, bounds.y);
  }

  /** 文档空间叠加预览：绘制中的贴图 + 四边形轮廓 + 角点手柄 + 网格线（角点齐全后）。 */
  drawOverlay(ctx: CanvasRenderingContext2D): void {
    if (this.#corners.length === 0) return;

    const plane = this.#plane;
    if (plane !== null) this.#drawPatchPreview(ctx, plane);

    ctx.save();
    ctx.strokeStyle = 'rgba(120,200,255,0.9)';
    ctx.fillStyle = 'rgba(120,200,255,0.9)';
    ctx.lineWidth = 1;

    if (plane !== null) {
      for (const [a, b] of planeGridLines(plane, 8)) {
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    }

    ctx.beginPath();
    ctx.moveTo(this.#corners[0]!.x, this.#corners[0]!.y);
    for (let i = 1; i < this.#corners.length; i++) ctx.lineTo(this.#corners[i]!.x, this.#corners[i]!.y);
    if (this.#corners.length === 4) ctx.closePath();
    ctx.stroke();
    for (const corner of this.#corners) {
      ctx.beginPath();
      ctx.arc(corner.x, corner.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}
